// dependency-graph-ignore-file

import {
  InlineRovoPromptTriggerSource,
  openInlineRovoPrompt,
} from '@databases/database-ai-first-creation-state/inline-rovo-prompt-state';
import {
  doClearEntireFieldsWithIds,
  doDuplicateEntireFieldsWithIds,
  doMoveFieldsWithIds,
} from '@databases/redux/contextual-menu/actions';
import { appStore } from '@databases/redux/store';
import { type DatabaseAdapter, type SelectionsApi } from '@databases/selections-api';
import { type DatabaseField } from '@databases/types/database';
import { type DatabaseView } from '@databases/types/database/view';
import {
  type ViewChangeType,
  ViewChangeTypes,
} from '@databases/utils/database/view/view';

import { type SelectFieldActionsAPI } from './select-fields-actions';

/**
 * Extract the column selection type from the discriminated union.
 */
type ResolvedColumnSelection = Extract<
  ReturnType<SelectionsApi['getResolvedSelection']>,
  { mode: 'column' }
>;

/**
 * Type guard that checks if the resolved selection is in column mode.
 */
function isColumnSelection(
  resolved: ReturnType<SelectionsApi['getResolvedSelection']>
): resolved is ResolvedColumnSelection {
  return resolved.mode === 'column';
}

/**
 * Gets field IDs from a column selection, or returns empty array if not in column mode.
 */
function getSelectedFieldIds(
  resolved: ReturnType<SelectionsApi['getResolvedSelection']>
): string[] {
  if (!isColumnSelection(resolved)) {
    return [];
  }

  return resolved.fields.map((f) => f.fieldId);
}

/**
 * Builds the action handlers for field-level operations in the action bar.
 */
export const buildSelectFieldsActionsForSelectionsAPI = ({
  contentId,
  selectionsApi,
  view,
  databaseAdapter,
  setViewChange,
}: {
  contentId: string;
  selectionsApi: SelectionsApi;
  view: DatabaseView;
  databaseAdapter: DatabaseAdapter;
  setViewChange: (view: ViewChangeType) => void;
}): SelectFieldActionsAPI => {
  const dispatch = appStore.dispatch;

  /**
   * Duplicates all selected fields and selects the newly created copies.
   */
  const handleDuplicateFields = () => {
    const fieldIds = getSelectedFieldIds(selectionsApi.getResolvedSelection());

    if (fieldIds.length === 0) {
      return;
    }

    const beforeFields = new Set(databaseAdapter.getVisibleFieldIds());

    dispatch(doDuplicateEntireFieldsWithIds(contentId, view.id, fieldIds));

    const afterFields = databaseAdapter.getVisibleFieldIds();
    const newFieldIds = afterFields.filter((id) => !beforeFields.has(id));

    if (newFieldIds.length > 0) {
      selectionsApi.selectFields(newFieldIds);
    } else {
      selectionsApi.deselectAll();
    }
  };

  /**
   * Clears the content of all selected fields without deleting them.
   */
  const handleClearFields = () => {
    const fieldIds = getSelectedFieldIds(selectionsApi.getResolvedSelection());

    if (fieldIds.length === 0) {
      return;
    }

    dispatch(doClearEntireFieldsWithIds(contentId, fieldIds));
  };

  /**
   * Moves the selected fields one position to the left.
   */
  const handleMoveFieldsLeft = () => {
    const fieldIds = getSelectedFieldIds(selectionsApi.getResolvedSelection());

    if (fieldIds.length === 0) {
      return;
    }

    dispatch(doMoveFieldsWithIds(contentId, view.settings, true, fieldIds));

    selectionsApi.selectFields(fieldIds);
  };

  /**
   * Moves the selected fields one position to the right.
   */
  const handleMoveFieldsRight = () => {
    const fieldIds = getSelectedFieldIds(selectionsApi.getResolvedSelection());

    if (fieldIds.length === 0) {
      return;
    }

    dispatch(doMoveFieldsWithIds(contentId, view.settings, false, fieldIds));

    selectionsApi.selectFields(fieldIds);
  };

  /**
   * Hides all selected fields from the current view.
   */
  const handleHideFields = () => {
    const fieldIds = getSelectedFieldIds(selectionsApi.getResolvedSelection());

    if (fieldIds.length === 0) {
      return;
    }

    const visibleFields = databaseAdapter
      .getVisibleFieldIds()
      .map((id) => databaseAdapter.getFieldById(id))
      .filter((f): f is DatabaseField => f !== undefined);

    fieldIds.forEach((fieldId) => {
      const viewChange: ViewChangeType = {
        type: ViewChangeTypes.VIEW_FIELDS,
        fieldKey: fieldId,
        fieldValue: false,
        fields: visibleFields,
      };

      setViewChange(viewChange);

      selectionsApi.deselectAll();
    });
  };

  /**
   * Opens the inline Rovo AI prompt for the selected field.
   */
  const handleAskRovo = () => {
    return openInlineRovoPrompt(InlineRovoPromptTriggerSource.TableField);
  };

  return {
    handleAskRovo,
    handleClearFields,
    handleDuplicateFields,
    handleHideFields,
    handleMoveFieldsLeft,
    handleMoveFieldsRight,
  };
};
